import { Component, EventEmitter, HostBinding, HostListener, Input, Optional, Output, Self, ViewEncapsulation } from '@angular/core';
import { NvAriaLabelDirective } from '../directives/nv-aria-label/nv-arial-label.directive';

/**
 *
 * A simple component that renders the New Visions logo as an `svg`.
 *
 * Use this wherever you need to use the logo.
 *
 * Resize this as you need by modifying the `height` property.
 */
@Component({
  selector: 'nv-logo',
  templateUrl: './nv-logo.component.html',
  styleUrls: ['./nv-logo.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class NvLogoComponent {
  @Input() type:
    | 'symbol'
    | 'symbol-invert'
    | 'wordmark'
    | 'portal'
    | 'portal-all-white'
    | 'family-portal-logo'
    | 'family-portal-logo-symbol-white-no-text'
    | 'ecfik-large-blue-symbol'
    | 'ecfik-large-white-symbol'
    | 'ecfik-small-blue-symbol'
    | 'ecfik-small-white-symbol';
  @Input() pointerOnHover: boolean = false;
  @Input() decorative: boolean = false;
  @Output() logoClick = new EventEmitter<MouseEvent>();

  constructor (
    @Optional() @Self() private nvAriaLabelDirective: NvAriaLabelDirective,
  ) {}

  private get isInteractive (): boolean {
    return this.pointerOnHover || this.logoClick.observed;
  }

  public get hasNvAriaLabelDirective (): boolean {
    return !!this.nvAriaLabelDirective;
  }

  @HostBinding('attr.role')
  get role (): string {
    if (this.decorative) {
      return null;
    }

    return this.isInteractive ? 'button' : 'img';
  }

  @HostBinding('attr.tabindex')
  get tabindex (): string {
    return this.isInteractive ? '0' : null;
  }

  @HostBinding('attr.aria-hidden')
  get ariaHidden (): string {
    return this.decorative ? 'true' : null;
  }

  @HostBinding('attr.aria-label')
  get hostAriaLabel (): string {
    if (this.decorative) {
      return null;
    }

    if (this.hasNvAriaLabelDirective) {
      return null;
    }

    return this.isInteractive ? 'Go to homepage' : 'Logo';
  }

  @HostListener('click', ['$event'])
  onLogoClick (event: MouseEvent): void {
    this.logoClick.emit(event);
  }

  @HostListener('keydown', ['$event'])
  onKeydown (event: KeyboardEvent): void {
    if (!this.isInteractive) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.logoClick.emit(new MouseEvent('click'));
    }
  }
}
